/* ==========================================================================
   NEFER Studio — Collections
   Hotspot binding and the bundle add. Reveals, breathing and the count-up all
   stay in nefer-motion.js; this file owns nothing else.

   Contract (collection handoff §6):
     - ONE active "look:handle" key for the whole page. Per-element state lets
       the drawing and the list drift out of sync.
     - Desktop: hover reveals, so a click on a dot passes straight through to
       the product. Touch: the first tap reveals the name, the second opens it.
     - Under 900px each label's authored side is ignored and derived from its
       x position, or a label near the right edge runs off a 390px screen.
     - The bundle button posts ONE cart/add.js with an items array. Sequential
       calls race and the drawer flickers.

   DOM contract:
     [data-nf-spot="look:handle"]   the dot button, + data-nf-url
     [data-nf-label="look:handle"]  the label link, + data-nf-x, data-nf-side
     [data-nf-row="look:handle"]    the list row link
     [data-nf-add-set="id,id,…"]    the bundle button
     [data-nf-set-msg]              quiet status line under that button
   ========================================================================== */

(function () {
  'use strict';

  var NARROW = '(max-width: 900px)';
  var HOVERS = '(hover: hover) and (pointer: fine)';
  var active = null;

  function each(list, fn) { Array.prototype.forEach.call(list, fn); }
  function $$(sel, root) { return (root || document).querySelectorAll(sel); }

  function keyed(key) {
    if (!key) return [];
    return $$(
      '[data-nf-spot="' + key + '"],' +
      '[data-nf-label="' + key + '"],' +
      '[data-nf-row="' + key + '"]'
    );
  }

  function setActive(key) {
    if (key === active) return;
    each(keyed(active), function (n) { n.classList.remove('is-active'); });
    active = key || null;
    each(keyed(active), function (n) { n.classList.add('is-active'); });
  }

  function keyOf(el) {
    return el.getAttribute('data-nf-spot') ||
           el.getAttribute('data-nf-label') ||
           el.getAttribute('data-nf-row');
  }

  /* ------------------------------------------------------------------ *
   * 1. Binding
   *    Hover is bound only where a real pointer exists. On touch the
   *    browser synthesises mouseenter just before click, which would make
   *    the key already active by the time the click lands and send the
   *    first tap straight to the product — exactly what §6.2 forbids.
   *    Focus is bound everywhere, so keyboard users get the label too.
   * ------------------------------------------------------------------ */

  function bind(root) {
    var canHover = window.matchMedia(HOVERS).matches;

    each($$('[data-nf-spot],[data-nf-label],[data-nf-row]', root), function (el) {
      if (el.__nfBound) return;
      el.__nfBound = 1;
      var key = keyOf(el);

      if (canHover) {
        el.addEventListener('mouseenter', function () { setActive(key); });
        el.addEventListener('mouseleave', function () { setActive(null); });
      }
      el.addEventListener('focusin', function () { setActive(key); });
      el.addEventListener('focusout', function () { setActive(null); });
    });

    each($$('[data-nf-spot]', root), function (dot) {
      if (dot.__nfClick) return;
      dot.__nfClick = 1;
      dot.addEventListener('click', function (e) {
        var key = dot.getAttribute('data-nf-spot');
        if (key === active) {
          window.location.href = dot.getAttribute('data-nf-url');
        } else {
          e.preventDefault();
          setActive(key);
        }
      });
    });
  }

  /* A stale active key survives a back-navigation from a product page, and
     the next single tap would then jump straight back out. Clear on leave. */
  window.addEventListener('pagehide', function () { setActive(null); });

  /* ------------------------------------------------------------------ *
   * 2. Label side
   *    Liquid renders the authored side inline, so the page is correct
   *    with JS off. This only rewrites it on narrow screens.
   * ------------------------------------------------------------------ */

  function applySides() {
    var narrow = window.matchMedia(NARROW).matches;
    each($$('[data-nf-label]'), function (lab) {
      var x = parseFloat(lab.getAttribute('data-nf-x'));
      if (isNaN(x)) return;
      var side = narrow ? (x <= 50 ? 'right' : 'left') : lab.getAttribute('data-nf-side');

      lab.classList.toggle('nf-spot__label--left', side === 'left');
      lab.classList.toggle('nf-spot__label--right', side === 'right');

      if (side === 'right') {
        lab.style.left = 'calc(' + x + '% + 14px)';
        lab.style.right = 'auto';
      } else {
        lab.style.right = 'calc(' + (100 - x) + '% + 14px)';
        lab.style.left = 'auto';
      }
    });
  }

  /* ------------------------------------------------------------------ *
   * 3. Bundle add
   *    One request for the whole set. If Shopify rejects the batch — one
   *    piece went out of stock between page load and the tap — fall back
   *    to adding them one at a time so the customer keeps the rest, and
   *    say so quietly rather than failing the whole action (§6.3).
   * ------------------------------------------------------------------ */

  function shop() { return window.NeferShop; }

  function initSets(root) {
    each($$('[data-nf-add-set]', root), function (btn) {
      if (btn.__nfSet) return;
      btn.__nfSet = 1;

      var msg = btn.parentNode.querySelector('[data-nf-set-msg]');
      var note = msg ? msg.textContent : '';

      btn.addEventListener('click', function () {
        var ids = btn.getAttribute('data-nf-add-set').split(',').filter(Boolean);
        if (!ids.length || !shop() || !shop().addItems) return;

        var label = btn.textContent;
        btn.disabled = true;
        btn.textContent = 'Adding…';

        var items = ids.map(function (id) { return { id: Number(id), quantity: 1 }; });

        function done(failed) {
          btn.disabled = false;
          btn.textContent = label;
          if (!msg) return;
          msg.textContent = failed
            ? failed + ' of these could not be added — the rest are in your cart. ' + note
            : note;
        }

        shop().addItems(items)
          .then(function () { done(0); })
          .catch(function () {
            /* Batch refused. Retry line by line and keep whatever lands. */
            var failed = 0;
            var chain = Promise.resolve();
            items.forEach(function (item) {
              chain = chain
                .then(function () { return shop().addItems([item]); })
                .catch(function () { failed += 1; });
            });
            chain.then(function () { done(failed); });
          });
      });
    });
  }

  /* ------------------------------------------------------------------ *
   * boot
   * ------------------------------------------------------------------ */

  function boot(root) {
    if (!document.querySelector('[data-nf-looks]')) return;
    bind(root);
    initSets(root);
    applySides();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { boot(); });
  } else {
    boot();
  }

  var resizeTimer;
  window.addEventListener('resize', function () {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(applySides, 150);
  });

  /* Theme editor re-renders the section without a page load. */
  document.addEventListener('shopify:section:load', function (e) {
    active = null;
    boot(e.target);
  });

  window.NeferLooks = { setActive: setActive, refresh: boot };
})();
